import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Mic, Users, Megaphone, Calendar, Video, Sparkles, ArrowRight } from "lucide-react";
import { GoldButton, GhostButton } from "@/components/site/ui";
import { useFetch } from "@/lib/useFetch";

const WORDS = ["PR", "PODCASTS", "EVENTS", "INFLUENCERS", "CONTENT", "COMMUNITY"];

export function EcoHero() {
    const nav = useNavigate();
    const [wi, setWi] = useState(0);
    useEffect(() => { const t = setInterval(() => setWi((i) => (i + 1) % WORDS.length), 1800); return () => clearInterval(t); }, []);
    const chips = [
        { icon: Mic, label: "Podcast Opportunity", top: "8%", left: "-3%" },
        { icon: Users, label: "500+ Audience", top: "60%", left: "-5%" },
        { icon: Megaphone, label: "Influencer Campaign", top: "4%", right: "-2%" },
        { icon: Sparkles, label: "Brand Featured", top: "70%", right: "-4%" },
    ];
    return (
        <section className="relative overflow-hidden bg-gradient-to-b from-teal-950 via-teal-900 to-teal-800 grain">
            <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-growth/10 blur-3xl" />
            <div className="absolute top-32 right-0 w-[28rem] h-[28rem] rounded-full bg-gold/10 blur-3xl" />
            <div className="container-x relative py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-6">
                    <div className="inline-flex items-center gap-2 rounded-full border border-brandborder bg-teal-700/40 px-3.5 py-1.5 text-xs font-semibold text-gold mb-6"><Sparkles className="w-3.5 h-3.5" /> Build Your Brand. Build Your Presence. Build Your Network.</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream">Your Brand <span className="gold-text">Deserves</span> to Be Seen.</h1>
                    <div className="mt-4 h-8 overflow-hidden">
                        <motion.span key={wi} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="font-mono font-bold text-growth text-xl tracking-wide">{WORDS[wi]}.</motion.span>
                    </div>
                    <p className="mt-5 text-base sm:text-lg text-cream-soft max-w-xl">SuDigi helps businesses build visibility, discover opportunities and connect with the people who can move their brand forward.</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <GoldButton onClick={() => nav("/build-my-brand")} data-testid="hero-build-brand">Build My Brand <ArrowRight className="w-4 h-4" /></GoldButton>
                        <GhostButton onClick={() => nav("/opportunities")} data-testid="hero-explore-opportunities">Explore Opportunities</GhostButton>
                    </div>
                    <p className="mt-6 text-sm text-cream-soft/70">Discover the people, platforms and opportunities that can put your brand in the spotlight.</p>
                </motion.div>
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-6 relative">
                    <div className="grid grid-cols-2 gap-3">
                        <img src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" alt="Podcast studio" loading="lazy" className="rounded-2xl h-44 w-full object-cover" />
                        <img src="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" alt="Business event stage" loading="lazy" className="rounded-2xl h-44 w-full object-cover mt-6" />
                        <img src="https://images.unsplash.com/photo-1695192699177-f98215c7bc4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" alt="Influencer recording" loading="lazy" className="rounded-2xl h-44 w-full object-cover -mt-3" />
                        <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" alt="Event audience" loading="lazy" className="rounded-2xl h-44 w-full object-cover mt-3" />
                    </div>
                    {chips.map((c) => (
                        <div key={c.label} className="hidden sm:flex absolute animate-floaty glass border border-brandborder/70 rounded-full px-3.5 py-1.5 text-xs font-semibold text-cream items-center gap-1.5" style={{ top: c.top, left: c.left, right: c.right }}>
                            <c.icon className="w-3.5 h-3.5 text-gold" /> {c.label}
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

function Counter({ to }) {
    const [n, setN] = useState(0);
    const ref = useRef(null);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) {
                let start = 0; const dur = 1200; const t0 = performance.now();
                const tick = (t) => { const p = Math.min(1, (t - t0) / dur); setN(Math.floor(p * to)); if (p < 1) requestAnimationFrame(tick); };
                requestAnimationFrame(tick); obs.disconnect();
            }
        }, { threshold: 0.4 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [to]);
    return <span ref={ref} className="font-mono font-bold text-3xl sm:text-4xl text-gold">{n.toLocaleString("en-IN")}+</span>;
}

export function TrustStats() {
    const { data } = useFetch("/mk/stats", {});
    const items = [
        { label: "Brands", value: data?.brands || 1000 }, { label: "Experts", value: data?.experts || 500 },
        { label: "Creators", value: data?.creators || 250 }, { label: "Events", value: data?.events || 100 },
        { label: "Podcasts", value: data?.podcasts || 50 },
    ];
    return (
        <section className="bg-teal-800 border-y border-brandborder/50 py-10" data-testid="trust-stats">
            <div className="container-x grid grid-cols-2 sm:grid-cols-5 gap-6 text-center">
                {items.map((it) => (<div key={it.label}><Counter to={it.value} /><div className="text-sm text-cream-soft mt-1">{it.label}</div></div>))}
            </div>
        </section>
    );
}
